import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Upload,
  Trash2,
  Wrench,
  UserCog,
  Check,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Page, PageHeader } from "@/components/common";
import ImportDialog from "@/components/ImportDialog";
import { useAuth } from "@/context/AuthContext";
import { TechBulkBar, TechDefaultsDialog, VIA } from "@/components/TechAssign";
import { FreeLabsGrid } from "@/components/FreeLabsGrid";
import { ManualBookingDialog } from "@/components/ManualBookingDialog";
import BookingDetail from "@/components/BookingDetail";
import { PrintButton } from "@/components/PrintButton";
import { api, errMsg, fmtDate } from "@/lib/api";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

const addWeeks = (isoDate, offset) => {
  const next = new Date(`${isoDate}T12:00:00`);
  next.setDate(next.getDate() + offset * 7);
  return next.toISOString().slice(0, 10);
};

const mondayFor = (isoDate) => {
  const current = new Date(`${isoDate}T12:00:00`);
  const mondayOffset = (current.getDay() + 6) % 7;
  current.setDate(current.getDate() - mondayOffset);
  return current.toISOString().slice(0, 10);
};

const dateHeader = (isoDate) => new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "short",
}).format(new Date(`${isoDate}T12:00:00`));

function CellEditor({ cell, meta, teacherId, week, onClose, onSaved }) {
  const existingClass = cell.entry
    ? meta.classes.find((schoolClass) => schoolClass.id === cell.entry.class_id)
    : null;
  const [f, setF] = useState({
    class_id: existingClass?.id || "",
    lab_id: cell.entry ? meta.labs.find((lab) => lab.name === cell.entry.lab)?.id : "",
    subject: cell.entry?.subject || "",
    class_size: cell.entry?.pupils ?? existingClass?.size ?? "",
  });
  const chooseClass = (classId) => {
    const schoolClass = meta.classes.find((item) => item.id === classId);
    setF({
      ...f,
      class_id: classId,
      class_size: schoolClass?.size ?? "",
    });
  };
  const save = async () => {
    const classSize = Number(f.class_size);
    if (!Number.isInteger(classSize) || classSize < 1) {
      toast.error("Enter a whole-number class size greater than zero");
      return;
    }
    try {
      await api.post("/timetable/entries", {
        teacher_id: teacherId,
        week,
        day: cell.day,
        period: cell.period,
        class_id: f.class_id,
        lab_id: f.lab_id,
        subject: f.subject,
      });
      await api.patch(`/timetable/classes/${f.class_id}/size`, { size: classSize });
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    await api.delete(`/timetable/entries/${cell.entry.id}`);
    onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="timetable-cell-dialog">
        <DialogHeader>
          <DialogTitle>
            Week {week} · {DAYS[cell.day - 1]} P{cell.period}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Class</Label>
            <Select value={f.class_id} onValueChange={chooseClass}>
              <SelectTrigger data-testid="timetable-class-select">
                <SelectValue placeholder="Choose class" />
              </SelectTrigger>
              <SelectContent>
                {meta.classes.map((schoolClass) => (
                  <SelectItem key={schoolClass.id} value={schoolClass.id}>
                    {schoolClass.name} ({schoolClass.size || "size not set"})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="timetable-class-size-input">Pupils in this class</Label>
            <Input
              id="timetable-class-size-input"
              min="1"
              onChange={(event) =>
                setF({ ...f, class_size: event.target.value })
              }
              step="1"
              type="number"
              value={f.class_size}
              data-testid="timetable-class-size-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Laboratory</Label>
            <Select
              value={f.lab_id}
              onValueChange={(value) => setF({ ...f, lab_id: value })}
            >
              <SelectTrigger data-testid="timetable-lab-select">
                <SelectValue placeholder="Choose lab" />
              </SelectTrigger>
              <SelectContent>
                {meta.labs.map((lab) => (
                  <SelectItem key={lab.id} value={lab.id}>
                    {lab.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Subject</Label>
            <Input
              value={f.subject}
              onChange={(event) => setF({ ...f, subject: event.target.value })}
              data-testid="timetable-subject-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {cell.entry && (
            <Button
              className="mr-auto text-destructive"
              onClick={remove}
              variant="ghost"
              data-testid="timetable-cell-delete"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              Remove
            </Button>
          )}
          <Button
            disabled={!f.class_id}
            onClick={save}
            data-testid="timetable-cell-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClassSizeDialog({ entry, onClose, onSaved }) {
  const [size, setSize] = useState(entry.pupils ?? "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const classSize = Number(size);
    if (!Number.isInteger(classSize) || classSize < 1) {
      toast.error("Enter a whole-number class size greater than zero");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/timetable/classes/${entry.class_id}/size`, { size: classSize });
      toast.success(`${entry.class_name} class size saved`);
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="class-size-dialog">
        <DialogHeader>
          <DialogTitle>{entry.class_name} class size</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="class-size-input">Pupils in this class</Label>
          <Input
            id="class-size-input"
            min="1"
            onChange={(event) => setSize(event.target.value)}
            step="1"
            type="number"
            value={size}
            data-testid="class-size-input"
          />
          <p className="text-sm text-muted-foreground" data-testid="class-size-help">
            Ask Jonny uses this to suggest the number of equipment sets for new bookings.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button
            disabled={saving}
            onClick={onClose}
            type="button"
            variant="outline"
            data-testid="class-size-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={saving}
            onClick={save}
            data-testid="class-size-save-button"
          >
            {saving ? "Saving…" : "Save class size"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TimetablePage() {
  const { user, can } = useAuth();
  const [searchParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [weekStart, setWeekStart] = useState("");
  const [calendarWeek, setCalendarWeek] = useState(null);
  const [teacherId, setTeacherId] = useState(user.id);
  const [grid, setGrid] = useState([]);
  const [cell, setCell] = useState(null);
  const [imp, setImp] = useState(false);
  const canManageAllTimetables = ["technician", "admin"].includes(user.role);
  const editable = teacherId === user.id || canManageAllTimetables;
  const canImport = canManageAllTimetables;
  const canAssign = can("booking:prep") || can("users:manage");
  const [assigning, setAssigning] = useState(false);
  const [selected, setSelected] = useState([]);
  const [techs, setTechs] = useState([]);
  const [defaults, setDefaults] = useState(false);
  const [view, setView] = useState("lessons");
  const [bookingSelection, setBookingSelection] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [cellBookings, setCellBookings] = useState([]);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [bookingTick, setBookingTick] = useState(0);
  const setupId = searchParams.get("staff") || "";
  const swipeStartX = useRef(null);
  useEffect(() => {
    if (canAssign) {
      api.get("/timetable/tech-setup").then((r) => setTechs(r.data.technicians));
    }
  }, [canAssign]);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const onCell = (c) => {
    if (assigning) {
      if (c.entry) toggle(c.entry.id);
    } else setCell(c);
  };
  const toggleBookingLesson = (entryId, lessonDate, checked) => {
    setBookingSelection((current) => {
      const isSelected = current.some(
        (selection) => (
          selection.entry_id === entryId && selection.date === lessonDate
        ),
      );
      if (checked && !isSelected) {
        return [...current, { entry_id: entryId, date: lessonDate }];
      }
      return current.filter(
        (selection) => (
          selection.entry_id !== entryId || selection.date !== lessonDate
        ),
      );
    });
  };
  const moveWeek = (offset) => {
    setWeekStart((current) => addWeeks(current, offset));
  };

  useEffect(() => {
    api.get("/timetable/meta").then((r) => {
      setMeta(r.data);
      setWeekStart(mondayFor(r.data.today));
      if (setupId && r.data.teachers.some((teacher) => teacher.id === setupId)) {
        setTeacherId(setupId);
      }
    });
  }, [setupId]);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (!weekStart) {
      return undefined;
    }
    let stale = false;
    api.get("/timetable/calendar-week", { params: { start: weekStart } }).then((r) => {
      if (!stale) {
        setCalendarWeek(r.data);
      }
    });
    return () => { stale = true; };
  }, [weekStart]);
  const activeWeek = calendarWeek?.week || "A";
  useEffect(() => {
    if (!calendarWeek) {
      return undefined;
    }
    if (!calendarWeek.week) {
      setGrid([]);
      return undefined;
    }
    let stale = false;
    api.get("/timetable/grid", { params: { week: activeWeek, teacher_id: teacherId } }).then((r) => {
      if (!stale) setGrid(r.data);
    });
    return () => { stale = true; };
  }, [activeWeek, calendarWeek, teacherId, tick]);
  useEffect(() => {
    if (!calendarWeek?.week) {
      setCellBookings([]);
      return undefined;
    }
    let stale = false;
    api
      .get("/timetable/week-bookings", {
        params: { start: calendarWeek.start, teacher_id: teacherId },
      })
      .then((response) => {
        if (!stale) {
          setCellBookings(response.data.items || []);
        }
      })
      .catch(() => {
        if (!stale) {
          setCellBookings([]);
        }
      });
    return () => {
      stale = true;
    };
  }, [bookingTick, calendarWeek, teacherId]);
  if (!meta) return <Page wide><p className="text-sm text-muted-foreground">Loading…</p></Page>;
  const selectedTeacher = meta.teachers.find((teacher) => teacher.id === teacherId);
  const staffSetup = selectedTeacher?.staff_setup ? selectedTeacher : null;
  const at = (d, p) => grid.find((g) => g.day === d && g.period === p);
  const ti = meta.today_info;
  const calendarDays = (calendarWeek?.days || []).map((day) => ({
    ...day,
    label: dateHeader(day.date),
  }));
  const bookingsForEntry = (entryId) => cellBookings.filter(
    (booking) => booking.entry_id === entryId,
  );

  return (
    <Page wide>
      <PageHeader
        title="Timetable"
        titleClassName="min-w-max"
        subtitleClassName="whitespace-nowrap"
        subtitle={ti.school_day ? `Today is ${fmtDate(meta.today)} — Week ${ti.week}` : `Today (${fmtDate(meta.today)}): ${ti.reason}`}
      >
        <PrintButton label="Print timetable" testId="timetable-print-button" />
        {meta.teachers.length > 0 && (
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="w-56" data-testid="timetable-teacher-select"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {canImport && <Button variant="outline" onClick={() => setImp(true)} data-testid="timetable-import-button"><Upload className="h-4 w-4 mr-2" />Import</Button>}
        {canImport && (
          <Button asChild variant="outline" data-testid="timetable-download-template-button">
            <a href="/ask-jonny-timetable-import-template.xlsx" download>
              <Download className="mr-2 h-4 w-4" />
              Download template
            </a>
          </Button>
        )}
        {canAssign && <Button variant="outline" onClick={() => setDefaults(true)} data-testid="tech-defaults-button"><UserCog className="h-4 w-4 mr-2" />Lab & teacher defaults</Button>}
        {canAssign && !assigning && view === "lessons" && <Button onClick={() => { setSelected([]); setAssigning(true); }} data-testid="tech-assign-mode-button"><Wrench className="h-4 w-4 mr-2" />Assign technicians</Button>}
      </PageHeader>
      {staffSetup && (
        <p
          className="mb-4 border-l-2 border-primary/50 bg-primary/5 px-3 py-2 text-sm"
          data-testid="timetable-staff-setup-notice"
        >
          Preparing {staffSetup.name.replace(" (setup)", "")}’s timetable. No invitation has
          been sent yet.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-1" data-testid="timetable-week-navigation">
          <Button
            aria-label="Previous week"
            onClick={() => moveWeek(-1)}
            size="icon"
            variant="outline"
            data-testid="timetable-previous-week-button"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-28 px-2 text-center text-sm font-semibold" data-testid="timetable-current-week-label">
            {calendarWeek?.week ? `Week ${calendarWeek.week}` : "No school week"}
          </span>
          <Button
            aria-label="Next week"
            onClick={() => moveWeek(1)}
            size="icon"
            variant="outline"
            data-testid="timetable-next-week-button"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            disabled={calendarWeek?.is_current_week}
            onClick={() => setWeekStart(mondayFor(meta.today))}
            size="sm"
            variant="ghost"
            data-testid="timetable-today-button"
          >
            Today
          </Button>
        </div>
        <Tabs value={view} onValueChange={(v) => { setView(v); setAssigning(false); setSelected([]); }}><TabsList data-testid="timetable-view-toggle"><TabsTrigger value="lessons" data-testid="timetable-view-lessons">Lessons</TabsTrigger><TabsTrigger value="free" data-testid="timetable-view-free">Free labs</TabsTrigger></TabsList></Tabs>
      </div>
      {view === "lessons" && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="timetable-editing-hint">
          To add or edit a lesson, click a lesson or free period.
        </p>
      )}
      {view === "lessons" && bookingSelection.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-3 border-y bg-muted/30 px-3 py-2"
          data-testid="timetable-booking-selection-bar"
        >
          <span className="text-sm font-medium" data-testid="timetable-booking-selection-count">
            {bookingSelection.length} lesson{bookingSelection.length === 1 ? "" : "s"} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setBookingOpen(true)}
              data-testid="timetable-book-selected-lessons-button"
            >
              <CalendarPlus className="mr-1.5 h-4 w-4" />
              Book selected lessons
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setBookingSelection([])}
              title="Clear selected lessons"
              aria-label="Clear selected lessons"
              data-testid="timetable-clear-booking-selection-button"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      {view === "free" ? <FreeLabsGrid week={calendarWeek?.week} periods={meta.periods} days={calendarDays} /> : (<>
      {assigning && (
        <>
          <p className="text-xs text-muted-foreground mb-2">Tick lessons (you can switch week or teacher and keep ticking), then pick a technician.
            <button className="underline ml-2" onClick={() => setSelected([...new Set([...selected, ...grid.map((g) => g.id)])])} data-testid="tech-select-all-visible">Select all shown</button></p>
          <TechBulkBar selected={selected} techs={techs} onApplied={() => { setSelected([]); load(); }} onCancel={() => { setAssigning(false); setSelected([]); }} />
        </>
      )}
      <div
        className="overflow-x-auto rounded-xl border bg-card"
        onTouchEnd={(event) => {
          const change = swipeStartX.current - event.changedTouches[0].clientX;
          if (Math.abs(change) > 60) moveWeek(change > 0 ? 1 : -1);
          swipeStartX.current = null;
        }}
        onTouchStart={(event) => { swipeStartX.current = event.touches[0].clientX; }}
        data-testid="timetable-week-swipe-area"
      >
        <table className="w-full min-w-[720px] text-sm table-fixed" data-testid="timetable-grid">
          <thead><tr className="border-b"><th className="p-3 text-left text-xs text-muted-foreground w-24">Period</th>{DAYS.map((d, index) => <th key={d} className={`p-3 text-left font-semibold ${calendarDays[index]?.is_today ? "bg-primary/10" : ""}`} data-testid={`timetable-day-header-${calendarDays[index]?.date || d}`}>{calendarDays[index]?.label || d}</th>)}</tr></thead>
          <tbody>{meta.periods.map((p) => (
            <tr key={p.period} className="border-b last:border-0">
              <td className="p-3 align-top"><div className="font-semibold">P{p.period}</div><div className="text-[11px] text-muted-foreground font-mono">{p.start}–{p.end}</div></td>
              {DAYS.map((_, di) => {
                const e = at(di + 1, p.period);
                const day = calendarDays[di];
                const sel = e && selected.includes(e.id);
                const active = day?.school_day && (editable || (assigning && e));
                const bookable = Boolean(
                  e && teacherId === user.id && can("booking:create"),
                );
                const bookingSelected = Boolean(
                  e && day && bookingSelection.some(
                    (selection) => (
                      selection.entry_id === e.id && selection.date === day.date
                    ),
                  ),
                );
                const cellBookingItems = e ? bookingsForEntry(e.id) : [];
                return (
                  <td key={di} className={`p-1.5 align-top ${day?.is_today ? "bg-primary/5" : ""}`}>
                    <div
                      className={`relative min-h-[64px] rounded-lg ${
                        sel
                          ? "border-2 border-primary bg-primary/25"
                          : e
                            ? "border border-primary/20 bg-primary/10"
                            : "border border-dashed border-transparent hover:border-border"
                      }`}
                    >
                      {bookable && (
                        <Checkbox
                          checked={bookingSelected}
                          className="absolute right-2 top-2 z-10 bg-background"
                          onCheckedChange={(checked) =>
                            toggleBookingLesson(e.id, day.date, Boolean(checked))
                          }
                          onClick={(event) => event.stopPropagation()}
                          aria-label={
                            `Book a practical for ${e.class_name} in period ${p.period}`
                          }
                          data-testid={`timetable-booking-checkbox-${e.id}-${day.date}`}
                        />
                      )}
                    <button
                      disabled={!active}
                      onClick={() => onCell({ day: di + 1, period: p.period, entry: e })}
                      className={`absolute inset-0 w-full rounded-lg ${
                        active ? "cursor-pointer" : "cursor-default"
                      }`}
                      aria-label={e ? `Edit ${e.class_name} lesson` : "Add timetable lesson"}
                      data-testid={`timetable-cell-${activeWeek}-${di + 1}-${p.period}`}
                    />
                    <div className={`relative pointer-events-none p-2 ${bookable ? "pr-8" : ""}`}>
                      {sel && (
                        <Check
                          className="absolute right-1.5 top-1.5 h-4 w-4 text-primary"
                          data-testid={`timetable-cell-selected-${e.id}`}
                        />
                      )}
                      {e && (
                        <>
                          <div className="text-sm font-semibold">{e.class_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {e.subject} · {e.lab}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {e.pupils ? `${e.pupils} pupils` : "Add class size"}
                          </div>
                          {e.technician && (
                            <div
                              className={
                                "mt-1 flex max-w-full items-center gap-1 rounded-full border "
                                + "bg-background/80 px-1.5 py-0.5 text-[10px]"
                              }
                              data-testid={`timetable-cell-tech-${e.id}`}
                            >
                              <Wrench className="h-2.5 w-2.5 shrink-0" />
                              <span className="truncate">{e.technician.name}</span>
                              {e.technician.via !== "lesson" && (
                                <span className="shrink-0 text-muted-foreground">
                                  · {e.technician.via}
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {!e && day && !day.school_day && (
                        <span
                          className="text-xs text-muted-foreground"
                          data-testid={`timetable-no-school-${day.date}`}
                        >
                          {day.reason}
                        </span>
                      )}
                    </div>
                    {cellBookingItems.length > 0 && (
                      <div className="relative space-y-1 border-t px-2 pb-2 pt-1">
                        {cellBookingItems.map((booking) => (
                          <button
                            key={booking.id}
                            type="button"
                            onClick={() => setBookingDetail(booking.id)}
                            className={
                              "block w-full truncate rounded bg-background/80 px-1.5 py-1 "
                              + "text-left text-xs font-medium text-primary hover:bg-background"
                            }
                            title={booking.title}
                            data-testid={`timetable-booked-practical-${booking.id}`}
                          >
                            {booking.title}
                          </button>
                        ))}
                      </div>
                    )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}</tbody>
        </table>
      </div>
      </>)}
      {cell && editable && (
        <CellEditor
          cell={cell}
          meta={meta}
          week={activeWeek}
          teacherId={teacherId}
          onClose={() => setCell(null)}
          onSaved={() => {
            setCell(null);
            load();
          }}
        />
      )}
      {cell && !editable && cell.entry && (
        <ClassSizeDialog
          entry={cell.entry}
          onClose={() => setCell(null)}
          onSaved={() => {
            setCell(null);
            load();
          }}
        />
      )}
      <ImportDialog
        kind="timetable"
        open={imp}
        onOpenChange={setImp}
        onDone={load}
        timetableTargetId={teacherId}
        timetableTargetName={selectedTeacher?.name.replace(" (setup)", "") || ""}
      />
      {canAssign && <TechDefaultsDialog open={defaults} onOpenChange={setDefaults} onChanged={load} />}
      <ManualBookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        onBooked={() => setBookingSelection([])}
        selectedLessonSlots={bookingSelection}
      />
      {bookingDetail && (
        <BookingDetail
          bookingId={bookingDetail}
          mode={can("booking:prep") && user.role !== "teacher" ? "tech" : "teacher"}
          onClose={() => setBookingDetail(null)}
          onChanged={() => setBookingTick((current) => current + 1)}
        />
      )}
    </Page>
  );
}
